'use client'

import { useState, useEffect, useRef, useCallback } from 'react'

interface SuburbOption {
  suburb: string
  postcode: string
  state: string
  label: string
  lat: number | null
  lng: number | null
}

interface SuburbSearchProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder?: string
  className?: string
  variant?: 'default' | 'hero'
}

function chipLabel(option: SuburbOption): string {
  return option.postcode ? `${option.suburb} ${option.postcode}` : option.suburb
}

export function SuburbSearch({
  value,
  onChange,
  onSubmit,
  placeholder = 'Search by suburb or postcode...',
  className,
  variant = 'default',
}: SuburbSearchProps) {
  const isHero = variant === 'hero'
  const [options, setOptions] = useState<SuburbOption[]>([])
  const [inputValue, setInputValue] = useState('')
  const [selected, setSelected] = useState<SuburbOption[]>([])
  const [filtered, setFiltered] = useState<SuburbOption[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const initRef = useRef(false)

  // Lazy-load suburbs list on first interaction
  const fetchedRef = useRef(false)
  const loadOptions = useCallback(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    fetch('/api/suburbs')
      .then((r) => r.json())
      .then((data: SuburbOption[]) => setOptions(data))
      .catch(() => {})
  }, [])

  // Load options eagerly if there's a URL value to restore
  useEffect(() => {
    if (value) loadOptions()
  }, [value, loadOptions])

  // On mount, restore selections from URL value
  useEffect(() => {
    if (initRef.current || !value || options.length === 0) return
    initRef.current = true

    const terms = value.split(',').map((t) => t.trim().toLowerCase())
    const restored: SuburbOption[] = []
    for (const term of terms) {
      if (!term) continue
      const match = options.find((o) => o.suburb.toLowerCase() === term)
      if (
        match &&
        !restored.some((r) => r.suburb === match.suburb && r.postcode === match.postcode)
      ) {
        restored.push(match)
      }
    }
    if (restored.length > 0) setSelected(restored)
  }, [value, options])

  // Filter options when input changes, excluding already-selected
  useEffect(() => {
    if (!inputValue.trim() || options.length === 0) {
      setFiltered([])
      setIsOpen(false)
      return
    }

    const selectedKeys = new Set(selected.map((s) => `${s.suburb}-${s.postcode}`))
    const term = inputValue.trim().toLowerCase()
    const matches = options.filter(
      (o) =>
        !selectedKeys.has(`${o.suburb}-${o.postcode}`) &&
        (o.suburb.toLowerCase().includes(term) || o.postcode.includes(term)),
    )
    setFiltered(matches.slice(0, 8))
    setIsOpen(matches.length > 0)
    setHighlightIndex(-1)
  }, [inputValue, options, selected])

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const submitSelection = useCallback(
    (newSelected: SuburbOption[]) => {
      const searchValue = newSelected.map((s) => s.suburb).join(',')
      onChange(searchValue)
      setTimeout(() => onSubmit(), 0)
    },
    [onChange, onSubmit],
  )

  const selectOption = useCallback(
    (option: SuburbOption) => {
      const next = [...selected, option]
      setSelected(next)
      setInputValue('')
      setIsOpen(false)
      submitSelection(next)
      setTimeout(() => inputRef.current?.focus(), 0)
    },
    [selected, submitSelection],
  )

  const removeOption = useCallback(
    (index: number) => {
      const next = selected.filter((_, i) => i !== index)
      setSelected(next)
      submitSelection(next)
      setTimeout(() => inputRef.current?.focus(), 0)
    },
    [selected, submitSelection],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.max(prev - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && filtered[highlightIndex]) {
        selectOption(filtered[highlightIndex])
      } else if (inputValue.trim()) {
        setIsOpen(false)
        onChange(inputValue)
        onSubmit()
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    } else if (e.key === 'Backspace' && !inputValue && selected.length > 0) {
      removeOption(selected.length - 1)
    }
  }

  return (
    <div ref={wrapperRef} className={`relative ${className || 'flex-1'}`}>
      <div
        className={
          isHero
            ? // Asana 005 (Figma 4054:31086) — muted fill on ALL hero tabs, matching the
              // Sell/Manage AddressSearch (bg-muted, no border, shadow-xs, green focus ring)
              'flex items-center flex-wrap gap-1.5 bg-muted rounded-sm shadow-xs px-3 py-1 min-h-9 cursor-text focus-within:ring-[3px] focus-within:ring-ring focus-within:ring-offset-0'
            : // Figma combobox Trigger 2198:36839 (as used on the listing pages, e.g. 4081:102388):
              // fill theme/muted #e0dfe0, NO border stroke, rounded-sm (2px), px-3, shadow-xs, h-9
              'flex items-center flex-wrap gap-1.5 bg-muted rounded-sm shadow-xs px-3 py-1 min-h-9 cursor-text'
        }
        onClick={() => inputRef.current?.focus()}
      >
        {selected.map((option, i) => (
          <span
            key={`${option.suburb}-${option.postcode}`}
            className="inline-flex items-center gap-1 bg-brand text-brand-foreground text-body-xs px-2.5 py-1 whitespace-nowrap"
          >
            {chipLabel(option)}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                removeOption(i)
              }}
              className="text-brand-foreground/70 hover:text-brand-foreground ml-0.5"
              aria-label="Remove"
            >
              &times;
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            loadOptions()
            if (inputValue.trim() && filtered.length > 0) setIsOpen(true)
          }}
          placeholder={selected.length === 0 ? placeholder : ''}
          className={`flex-1 min-w-[80px] bg-transparent text-body-sm text-foreground focus:outline-none ${
            isHero
              ? 'placeholder:text-muted-foreground'
              : // Figma Trigger 4081:102388 binds the label to theme/foreground, not muted-foreground
                'placeholder:text-foreground'
          }`}
          autoComplete="off"
        />
        {isHero ? (
          // Search icon — matches the Sell/Manage AddressSearch input
          <svg
            className="shrink-0 text-brand/40 ml-2"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
        ) : (
          // chevrons-up-down affordance per Figma combobox trigger
          <svg
            className="shrink-0 pointer-events-none text-foreground"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m7 15 5 5 5-5" />
            <path d="m7 9 5-5 5 5" />
          </svg>
        )}
      </div>
      {isOpen && filtered.length > 0 && (
        <ul className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border border-border-dark shadow-md max-h-64 overflow-y-auto rounded-sm">
          {filtered.map((option, i) => (
            <li
              key={`${option.suburb}-${option.postcode}`}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                selectOption(option)
              }}
              onMouseEnter={() => setHighlightIndex(i)}
              className={`px-3 py-2.5 text-body-sm cursor-pointer ${
                i === highlightIndex
                  ? // Figma menu item 2198:36854 highlighted state: theme/secondary fill, theme/foreground text
                    'bg-secondary text-foreground'
                  : 'text-foreground hover:bg-secondary'
              }`}
            >
              <span className="font-medium">{option.suburb}</span>
              {option.postcode && (
                <span
                  className={i === highlightIndex ? 'text-foreground/70' : 'text-muted-foreground'}
                >
                  {' '}
                  {option.postcode}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
